import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Video, CalendarDays, Clock, MonitorPlay } from 'lucide-react';

import type { HomeNextSession } from '@/lib/api/home';

function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'Starting now';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `Starts in ${m} min`;
  const h = Math.floor(m / 60);
  return h < 24 ? `Starts in ${h}h ${m % 60}m` : `Starts in ${Math.floor(h / 24)}d`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const isToday = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return isToday
    ? `Today, ${time}`
    : `${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}, ${time}`;
}

export function BatchNextActionCard({ session, batchId }: { session: HomeNextSession; batchId: string }) {
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const start = session.scheduled_at ? new Date(session.scheduled_at).getTime() : 0;
  const diff = start - now;
  const isLive = !!session.scheduled_at && diff <= 0 && diff > -2 * 60 * 60 * 1000;

  const viewDetails = () => {
    if (session.topic_id) {
      navigate({ to: '/courses/$batchId/topics/$topicId', params: { batchId, topicId: session.topic_id }, search: { tab: 'overview' } });
    } else {
      navigate({ to: '/schedule' });
    }
  };

  return (
    <div
      className="relative flex flex-col gap-6 overflow-hidden rounded-[24px] p-6 sm:min-h-[250px] sm:flex-row sm:items-center sm:gap-8 sm:p-8"
      style={{ background: 'linear-gradient(167.74deg, #fff5f0 0%, #faf5ff 40%, #f3e8ff 100%)' }}
    >
      <div aria-hidden="true" className="pointer-events-none absolute left-[-40px] top-[130px] size-40 rounded-full opacity-[0.15]"
        style={{ background: 'radial-gradient(circle, #ff6b3d 0%, transparent 70%)' }} />

      <div className="flex items-center gap-2 sm:absolute sm:left-8 sm:top-6">
        <span className={`size-2 rounded-full ${isLive ? 'animate-pulse bg-[#22c55e]' : 'bg-[#ff5a1f]'}`} />
        <span className={`text-[10px] font-bold uppercase leading-[15px] tracking-[1px] ${isLive ? 'text-[#16a34a]' : 'text-[#ff5a1f]'}`}>
          Your Next Action
        </span>
      </div>

      <div className="flex size-20 shrink-0 items-center justify-center rounded-[20px] bg-[#8b5cf6]/10 sm:mt-4 sm:size-[120px]">
        <Video className="size-9 text-[#8b5cf6] sm:size-[52px]" />
      </div>

      <div className="flex min-w-0 flex-1 flex-col sm:pt-4">
        <p className="text-xs font-semibold uppercase leading-4 tracking-[0.3px] text-[#9ca3af]">
          {isLive ? 'Live session in progress' : 'Live session starting soon'}
        </p>
        <h2 className="pt-1 text-[28px] font-bold leading-tight tracking-tight text-[#111827] sm:text-[42px] sm:leading-[52.5px]">
          {session.title || 'Live Session'}
        </h2>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 pt-4 text-sm text-[#6b7280]">
          {session.scheduled_at && (
            <span className="flex items-center gap-1.5"><CalendarDays className="size-3.5" />{fmtDate(session.scheduled_at)}</span>
          )}
          {session.scheduled_at && (
            <span className="flex items-center gap-1.5"><Clock className="size-3.5" />{isLive ? 'In progress' : fmtCountdown(diff)}</span>
          )}
          <span className="flex items-center gap-1.5"><MonitorPlay className="size-3.5" />Live on KodPrep</span>
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-3 sm:w-[199px] sm:pt-4">
        {session.meeting_url ? (
          <a
            href={session.meeting_url}
            target="_blank"
            rel="noreferrer"
            className="flex h-14 items-center justify-center rounded-[14px] px-7 text-sm font-semibold text-white shadow-[0px_10px_7.5px_rgba(0,0,0,0.1),0px_4px_3px_rgba(0,0,0,0.1)] transition-opacity hover:opacity-90"
            style={{ background: 'linear-gradient(164.26deg, #ff6b3d 0%, #ff4d1a 100%)' }}
          >
            Join Session
          </a>
        ) : (
          <button
            onClick={viewDetails}
            className="flex h-14 items-center justify-center rounded-[14px] px-7 text-sm font-semibold text-white shadow-[0px_10px_7.5px_rgba(0,0,0,0.1),0px_4px_3px_rgba(0,0,0,0.1)] transition-opacity hover:opacity-90"
            style={{ background: 'linear-gradient(164.26deg, #ff6b3d 0%, #ff4d1a 100%)' }}
          >
            Join Session
          </button>
        )}
        <button
          onClick={viewDetails}
          className="flex h-14 items-center justify-center rounded-[14px] border border-[#ececec] bg-white px-7 text-sm font-semibold text-[#111827] transition-colors hover:bg-[#f9fafb]"
        >
          View Session Details
        </button>
      </div>
    </div>
  );
}
